import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFlags, useTournamentResults } from '../api/hooks';
import { Loader, ErrorView, EmptyView } from '../components/StateViews';
import { BackIcon } from '../components/icons';
import { backButton, haptics } from '../telegram';
import { DeckBox } from '../components/pass/DeckBox';
import { RARITY_LABEL, RARITY_LABEL_M, rarityTextStyle, rarityTint } from '../components/cosmetics/cosmeticsMap';
import { CosmeticAvatar } from '../components/cosmetics/CosmeticAvatar';
import { NickFx } from '../components/cosmetics/NickFx';
import { RowFxLayer, rowFxClass } from '../components/cosmetics/RowFx';
import { shortDate } from '../format';
import { markShownLevel } from '../components/pass/level';

/** "Результат турнира" — final standings + rating points. */
export function TournamentResultScreen() {
  const { id } = useParams();
  const tid = Number(id);
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useTournamentResults(tid);
  const flags = useFlags();

  // Секвенция 6.3 (Handoff 3): играет один раз на игру (resultsSeen),
  // reduced-motion — всё сразу. step: 0 старт … 6 всё показано.
  const [step, setStep] = useState(0);
  const [pts, setPts] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  // Уровень справа и count-up «+N XP» — синхронно с полосой (handoff 4 §2.2).
  const [lvlShown, setLvlShown] = useState<number | null>(null);
  const [xpShown, setXpShown] = useState<number | null>(null);
  const seqStarted = useRef(false);
  useEffect(() => {
    if (!data || seqStarted.current) return;
    seqStarted.current = true;
    const key = 'pass_results_seen';
    let seen: number[] = [];
    try {
      seen = JSON.parse(localStorage.getItem(key) ?? '[]') as number[];
    } catch {
      /* приватный режим */
    }
    const reduce =
      typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    const target = data.rows.find((r) => r.isMe)?.points ?? 0;
    const p = data.pass;
    const markPass = () => {
      const lvl = p?.level;
      if (p && lvl != null) markShownLevel(lvl, (lvl - 1) * 100 + Math.round((p.xpFrac ?? 0) * 100));
    };
    if (reduce || seen.includes(tid)) {
      setStep(6);
      setPts(target);
      markPass();
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify([...seen.slice(-30), tid]));
    } catch {
      /* ок */
    }
    // count-up 0–500 мс
    const t0 = performance.now();
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / 500);
      setPts(Math.round(target * (1 - (1 - k) * (1 - k))));
      if (k < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    const timers = [
      window.setTimeout(() => setStep(1), 300), // карточка колоды
      window.setTimeout(() => {
        setStep(2); // строка апгрейда
        haptics.light();
      }, 800),
      window.setTimeout(() => setStep(3), 1000), // карточка Straddle Pass
      window.setTimeout(() => {
        setStep(4); // полоса растёт
        haptics.light();
      }, 1150),
      window.setTimeout(() => setStep(5), 1900), // подпись гарантии
      window.setTimeout(() => setStep(6), 2100), // таблица
    ];
    if (p && p.levelsGained > 0 && p.level != null) {
      const lvl = p.level;
      const gained = Math.round(p.xpGained ?? p.levelsGained * 100);
      const startLevel = Math.max(1, lvl - Math.ceil(p.levelsGained));
      const crossings = lvl - startLevel;
      setLvlShown(startLevel);
      // count-up XP вместе с полосой: 1150–1900 (750 мс)
      timers.push(
        window.setTimeout(() => {
          const s0 = performance.now();
          const tick2 = (now: number) => {
            const k = Math.min(1, (now - s0) / 750);
            setXpShown(Math.round(gained * k));
            if (k < 1) requestAnimationFrame(tick2);
          };
          requestAnimationFrame(tick2);
        }, 1150),
      );
      // flip числа на каждом пересечении целого; ≥3 уровней — один flip (§2.4)
      if (crossings >= 3) {
        timers.push(
          window.setTimeout(() => {
            setLvlShown(lvl);
            haptics.light();
          }, 1150 + 375),
        );
      } else {
        for (let i = 1; i <= crossings; i++) {
          timers.push(
            window.setTimeout(
              () => {
                setLvlShown(startLevel + i);
                haptics.light();
              },
              1150 + Math.round((750 * i) / Math.max(1, crossings)),
            ),
          );
        }
      }
      timers.push(window.setTimeout(markPass, 1900));
    } else {
      markPass();
    }
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [data, tid]);

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (isLoading) return <Loader />;
  if (isError || !data) return <ErrorView message={(error as Error)?.message} />;

  const { game, rows } = data;
  if (rows.length === 0) return <EmptyView message="Нет результатов по этому турниру" />;

  const me = rows.find((r) => r.isMe);

  return (
    <div>
      <div data-theme="dark" className="detail-hero">
        <div className="hero-top">
          <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
            <BackIcon />
          </button>
        </div>
        <div className="hero-title" style={{ fontSize: 20 }}>
          {game.title ?? game.format ?? 'Турнир'}
        </div>
        <div className="hero-meta">
          {shortDate(game.startTime)}
          {game.format ? ` · ${game.format}` : ''} · {game.fieldSize} игроков
        </div>
      </div>

      {me && (
        <div className="result-summary">
          <div className="place">
            <span className="num">{me.position ?? '—'}</span>
            <span className="lbl">место</span>
            {me.position !== null && me.position <= 9 && (
              <span className="bp-ft-badge">Финальный стол</span>
            )}
          </div>
          <div className="vdiv" />
          <div className="cell">
            <div className={`v${step >= 1 || pts >= me.points ? ' green' : ''}`}>
              {me.points > 0 ? `+${pts}` : '—'}
            </div>
            <div className="l">очков</div>
          </div>
          <div className="cell">
            <div className="v">{game.fieldSize}</div>
            <div className="l">игроков</div>
          </div>
        </div>
      )}

      {/* 6.3: Straddle Pass — получена колода + прирост уровней */}
      {Boolean(flags.data?.pass) && data.pass && collapsed && (
        <button className="bp-result-collapsed" onClick={() => navigate('/pass/decks')}>
          Колода в инвентаре · {RARITY_LABEL[data.pass.rarity]} ›
        </button>
      )}
      {Boolean(flags.data?.pass) && data.pass && !collapsed && (
        <>
          <div
            className={`bp-result-card bp-seq${step >= 1 ? ' in' : ''}`}
            style={{
              borderColor: rarityTint(data.pass.rarity, 0.55),
              background: `linear-gradient(170deg, ${rarityTint(data.pass.rarity, 0.24)}, ${rarityTint(data.pass.rarity, 0.04)} 70%)`,
            }}
          >
            <span className="bp-result-shine" aria-hidden />
            <DeckBox rarity={data.pass.rarity} stage="idle" size={50} />
            <div className="bp-result-main">
              {/* макет 6.3: подпись цветом редкости, название белым 18px */}
              <div className="bp-overline rtx" style={rarityTextStyle(data.pass.rarity)}>
                ПОЛУЧЕНА КОЛОДА
              </div>
              <div className="nm">{RARITY_LABEL[data.pass.rarity]} колода</div>
              {data.pass.upgraded && data.pass.rarity > 0 && (
                <div className={`bp-result-upg bp-seq${step >= 2 ? ' in' : ''}`}>
                  {/* макет: зелёная только стрелка, текст обычный */}
                  <svg className="arr" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1f9d6b" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M12 19V5M5 12l7-7 7 7" />
                  </svg>
                  {RARITY_LABEL_M[data.pass.rarity - 1]} → {RARITY_LABEL_M[data.pass.rarity].toLowerCase()} за финальный стол
                </div>
              )}
              <div className="bp-result-actions">
                {data.pass.opened ? (
                  <Link className="bp-result-btn bp-result-btn--dim" to="/pass/decks">
                    Открыта
                  </Link>
                ) : (
                  <>
                    <Link className="bp-result-btn" to={`/pass/decks/${data.pass.deckId}/open`}>
                      Открыть сейчас
                    </Link>
                    <button className="bp-result-btn bp-result-btn--dim" onClick={() => setCollapsed(true)}>
                      В инвентарь
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          {data.pass.levelsGained > 0 && data.pass.level !== null && (
            <Link className={`bp-result-xp bp-seq${step >= 3 ? ' in' : ''}`} to="/pass">
              <span className="row">
                <b>
                  +{xpShown ?? Math.round(data.pass.xpGained ?? data.pass.levelsGained * 100)} XP
                  Straddle Pass
                </b>
              </span>
              <span className="barwrap mono">
                <span className="lv">{Math.max(1, (data.pass.level ?? 1) - Math.ceil(data.pass.levelsGained))}</span>
                <span className="bar">
                  {(() => {
                    // Двухцветная полоса (handoff 4 §2.2): «было» — серый сегмент,
                    // золотой прирост растёт следом; при пересечении уровня
                    // старт с нуля текущего уровня.
                    const end = data.pass.xpFrac ?? 0;
                    const gained = (data.pass.xpGained ?? data.pass.levelsGained * 100) / 100;
                    const was = Math.max(0, end - gained);
                    return (
                      <>
                        <i className="was" style={{ width: `${Math.round(was * 100)}%` }} />
                        <i
                          className="now"
                          style={{
                            left: `${Math.round(was * 100)}%`,
                            width: step >= 4 ? `${Math.round((end - was) * 100)}%` : '0%',
                          }}
                        />
                      </>
                    );
                  })()}
                </span>
                <span className={`lv lv--flip${step >= 4 ? ' in' : ''}`} key={lvlShown ?? data.pass.level}>
                  {lvlShown ?? data.pass.level}
                </span>
              </span>
              {(data.pass.questsDone ?? []).map((q, i) => (
                <span key={i} className={`bp-result-quest bp-seq${step >= 5 ? ' in' : ''}`}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1f9d6b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M4 12.5 9.5 18 20 6.5" />
                  </svg>
                  Задание выполнено: {q.title} · +{q.reward} XP
                </span>
              ))}
              <span className={`hint mono bp-seq${step >= 5 ? ' in' : ''}`}>
                {(data.pass.guaranteeIn ?? 0) > 0
                  ? `Ещё ${data.pass.guaranteeIn} ${data.pass.guaranteeIn === 1 ? 'игра' : (data.pass.guaranteeIn ?? 0) < 5 ? 'игры' : 'игр'} — и эпическая колода гарантирована`
                  : 'Награда ждёт на дорожке'}
              </span>
            </Link>
          )}
        </>
      )}

      <div className={`bp-seq${step >= 6 ? ' in' : ''}`}>
      <div className="lb-head">
        <span>Место · Игрок</span>
        <span>Очки рейтинга</span>
      </div>
      <div className="card" style={{ padding: '4px 6px' }}>
        {rows.map((r) => (
          <Link
            className={`lb-row ${r.isMe ? 'me' : ''}${rowFxClass(r.cosmetics?.row)}`}
            key={r.playerId}
            to={`/players/${r.playerId}`}
          >
            <RowFxLayer cosmetic={r.cosmetics?.row} height={44} />
            <div className="lb-rank">{r.position ?? '—'}</div>
            <CosmeticAvatar name={r.name} src={r.avatarUrl} size={28} frame={r.cosmetics?.frame} />
            <div className="lb-name">
              <NickFx cosmetic={r.cosmetics?.nick}>{r.name}</NickFx>
              {r.position === 1 ? ' ♛' : ''}
            </div>
            <div className="lb-points">
              {r.points > 0 ? `+${r.points}` : '—'}
              {/* Баунти-очки отдельно (№17): игроки не понимали, что баунти
                  уже в сумме — показываем состав, когда он есть */}
              {(r.bountyPoints ?? 0) > 0 && (
                <div className="lb-points-split">
                  место {r.placePoints ?? 0} + КО {r.bountyPoints}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
      </div>
    </div>
  );
}
